import { useSuspenseQuery } from '@tanstack/react-query';
import type { AccountsPartnerAccountInfo } from '@customerio/hydra-ui-api';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { accountKeys, parseAccount, partnerAccountsQueryKey, type Account } from './entity';

export type { AccountsPartnerAccountInfo };
export { partnerAccountsQueryKey };

const STALE_TIME = 60_000;

/** @public Fetch the full account record (includes billing-contact emails). */
export async function getAccount(accountId: number): Promise<Account> {
  const data = await hydraUiApi.accounts.get({
    params: { path: { account_id: String(accountId) } },
  });
  if (data.account == null) {
    throw new Error(`Account ${accountId} not found`);
  }
  return parseAccount(data.account);
}

export function getAccountQueryOptions(accountId: number) {
  return {
    queryKey: accountKeys.detail(accountId, String(accountId)),
    queryFn: () => getAccount(accountId),
    staleTime: STALE_TIME,
  };
}

/** @public Suspense read of the full account record. */
export function useAccount(accountId: number) {
  return useSuspenseQuery(getAccountQueryOptions(accountId));
}

/**
 * usePartnerAccounts lists the accounts the current partner has provisioned via
 * the partner signup flow. Backed by GET /v1/accounts/:account_id/partner_accounts,
 * gated on the same partner_account attribute + account/edit permission as the
 * create endpoint. The response wraps the rows as `{ accounts }`; callers read `.accounts`.
 */
export function usePartnerAccounts(accountId: number) {
  return useHydraUIAPIQuery({
    queryKey: partnerAccountsQueryKey(accountId),
    queryFn: ({ signal }) =>
      hydraUiApi.accounts.getPartnerAccounts({
        params: { path: { account_id: String(accountId) } },
        signal,
      }),
  });
}
